import * as React from "react";
import {useMemo} from "react";
import {Button, Segment} from "semantic-ui-react";
import AceEditor from "react-ace";
import {QueuedEvent} from "../../../../../generated";
import {notify} from "react-notify-toast";

export interface QueuedEventJsonViewerProps {
    queuedEvent: QueuedEvent | null;
    readOnly?: boolean;
}

export function QueuedEventJsonViewer(props: QueuedEventJsonViewerProps) {
    const {queuedEvent} = props;

    const queuedEventJson = useMemo(() => {
        return queuedEvent ? JSON.stringify(queuedEvent, null, 2) : "{}";
    }, [queuedEvent]);

    const eventId = queuedEvent?._id ?? null;
    const transportOrderId = queuedEvent?.transportOrderEvent?.transportOrder?._id ??
        queuedEvent?.etaEvent?.transportOrder?._id ??
        (queuedEvent as any)?.transportOrder?._id ??
        null;

    const eventType = useMemo(() => {
        if (!queuedEvent) return "Unknown";
        if (queuedEvent.etaEvent) return "ETA_EVENT";
        if (queuedEvent.transportOrderEvent?.change?.type?.toString().includes("ATTACHMENT"))
            return "ATTACHMENT_EVENT";
        if (queuedEvent.transportOrderEvent) return "TRANSPORT_ORDER_STATUS_EVENT";
        return "UNSUPPORTED";
    }, [queuedEvent]);

    const CopyableID = ({label, value}: {label: string, value: string}) => (
        <div style={{display: 'flex', alignItems: 'center', marginBottom: '0.25em'}}>
            <span style={{fontWeight: 'bold', marginRight: '0.5em'}}>{label}</span>
            <span style={{marginRight: '0.4em'}}>{value}</span>
            <Button
                icon="copy"
                size="mini"
                basic
                compact
                title={`Copy full ${label}`}
                onClick={() => {
                    navigator.clipboard.writeText(value);
                    notify.show(`${label} copied to clipboard`, 'success', 1500);
                }}
                style={{padding: '0.25em', height: '1.6em'}}
            />
        </div>
    );

    return (
        <Segment
            basic
            style={{
                padding: "0.5em",
                backgroundColor: "#f7f7f7",
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
            }}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.4em",
                    marginBottom: "0.75em",
                    paddingBottom: "0.5em",
                    borderBottom: "1px solid #ddd",
                }}
            >
                {eventId && <CopyableID label="Queued Event ID:" value={eventId}/>}
                {transportOrderId && <CopyableID label="Transport Order ID:" value={transportOrderId}/>}
                {eventType && (
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <span style={{fontWeight: 'bold', marginRight: '0.5em'}}>Event Type:</span>
                        <span style={{fontSize: '0.85em'}}>{eventType}</span>
                    </div>
                )}
            </div>

            <AceEditor
                mode="json"
                theme="monokai"
                name={`queued-event-json-${eventId}`}
                value={queuedEventJson}
                readOnly={props.readOnly ?? true}
                width="100%"
                fontSize={12.5}
                style={{
                    flex: "1",
                    display: "flex",
                }}
                setOptions={{
                    useWorker: false,
                    showPrintMargin: false,
                    highlightActiveLine: true,
                    wrap: true,
                    fadeFoldWidgets: true,
                }}
            />
        </Segment>
    );
}
